/**
 * KI-Assistent - Floating Chat mit Sprache, Aktionen, Vorlesen
 * - Spracheingabe (Web Speech API - lokal im Browser)
 * - Vorlesen (Web Speech API TTS - lokal)
 * - Aufgaben erstellen/bearbeiten/erledigen
 * - Schriftstücke formulieren und nach Bestätigung senden
 * - Datei-Upload direkt im Chat (wie Posteingang)
 * - Verschiebbarer Floating Button mit farbigen Benachrichtigungen
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  X, Send, Shield, Mic, MicOff, Volume2, VolumeX,
  MessageSquarePlus, Loader2, Lock, CheckCircle, AlertTriangle,
  Paperclip, FileText, Image as ImageIcon, Lightbulb, ExternalLink,
  AlertCircle, Mail, Bot, ChevronDown, RotateCw, History, Star
} from 'lucide-react';
import ZeoIcon from './ZeoIcon';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { betreuungAPI } from '@/api/betreuungAPI';
import { authAPI } from '@/api/authAPI';
import { useTabs } from '@/contexts/TabContext';
import { toast } from 'sonner';
import FloatingKIButton from './FloatingKIButton';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function AssistentChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [pendingAktion, setPendingAktion] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [aktiveDokumentKontext, setAktiveDokumentKontext] = useState(null); // Für Dokument-Bearbeitung
  const [uploadingFile, setUploadingFile] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);  // Automatisches Vorlesen (default: AN)
  const [audioUnlocked, setAudioUnlocked] = useState(false);  // Für Browser-Audio-Freigabe
  const [briefingLoaded, setBriefingLoaded] = useState(false);  // Morgen-Briefing geladen?
  const [musterVorschlaege, setMusterVorschlaege] = useState(0);  // Anzahl neuer Mustervorschläge
  const [hasShownVorschlaegeBanner, setHasShownVorschlaegeBanner] = useState(
    () => sessionStorage.getItem('athena_muster_banner_shown') === 'true'
  );
  const [isRecording, setIsRecording] = useState(false);  // OpenAI Whisper Recording
  const [conversationMode, setConversationMode] = useState(false);  // Gesprächsmodus: Sprechen → Zeo antwortet → hört wieder zu
  const [selectedVoice, setSelectedVoice] = useState(() => localStorage.getItem('zeo_voice') || 'thorsten-high');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  
  // Benachrichtigungen für den Floating Button
  const [notifications, setNotifications] = useState({
    aufgaben: 0,      // Rot: Neue Aufgaben
    nachrichten: 0,   // Orange: Neue Nachrichten
    erledigt: 0       // Grün: Erledigte/KI-Aktionen
  });
  const [dismissedNotifications, setDismissedNotifications] = useState(false);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Loading-Phasen Animation
  useEffect(() => {
    if (!loading) { setLoadingPhase(0); return; }
    const phases = ['Zeo arbeitet...', 'Daten werden geladen...', 'Tools werden ausgefuehrt...', 'Analyse laeuft...', 'Antwort wird erstellt...'];
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % phases.length;
      setLoadingPhase(i);
    }, 2500);
    return () => clearInterval(interval);
  }, [loading]);

  // Stimmen laden + ElevenLabs Status pruefen
  const [elevenLabsActive, setElevenLabsActive] = useState(false);
  const [elevenLabsVoices, setElevenLabsVoices] = useState([]);
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const token = localStorage.getItem('olympus_token');
        const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
        // ElevenLabs Status pruefen
        const statusResp = await fetch(`${API_URL}/api/voice/status`, { headers: authHeaders });
        if (statusResp.ok) {
          const statusData = await statusResp.json();
          if (statusData.tts?.aktiv) {
            setElevenLabsActive(true);
            // ElevenLabs-Stimmen laden
            try {
              const elResp = await fetch(`${API_URL}/api/voice/voices`, { headers: authHeaders });
              if (elResp.ok) {
                const elData = await elResp.json();
                if (elData.length > 0) {
                  setElevenLabsVoices(elData);
                  // Erste ElevenLabs-Stimme als Standard setzen
                  const savedVoice = localStorage.getItem('zeo_voice_eleven');
                  if (!savedVoice) {
                    localStorage.setItem('zeo_voice_eleven', elData[0].voice_id);
                  }
                }
              }
            } catch (e) { /* ElevenLabs Stimmen nicht ladbar */ }
          }
        }
        // Piper-Stimmen als Fallback laden
        const resp = await fetch(`${API_URL}/api/sprache/stimmen`, { headers: authHeaders });
        if (resp.ok) {
          const data = await resp.json();
          if (data.stimmen) setAvailableVoices(data.stimmen);
        }
      } catch (e) { /* Stimmen nicht verfuegbar */ }
    };
    loadVoices();
  }, []);
  
  // Tab-System
  const { openTab } = useTabs();
  
  // ====== BENACHRICHTIGUNGEN LADEN ======
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const heute = new Date().toISOString().slice(0, 10);
        
        // Aufgaben einmal laden (für rot + grün)
        const aufgabenResp = await fetch(`${API_URL}/api/betreuung/aufgaben?limit=100`);
        if (aufgabenResp.ok) {
          const aufgaben = await aufgabenResp.json();
          // Rot: Neue/fällige Aufgaben heute
          const neueAufgaben = aufgaben.filter(a => 
            a.status === 'offen' && (
              a.erstellt_am?.slice(0, 10) === heute || 
              a.created_date?.slice(0, 10) === heute ||
              a.faellig_am?.slice(0, 10) === heute ||
              a.faelligkeit?.slice(0, 10) === heute
            )
          );
          // Grün: Heute erledigte Aufgaben
          const heuteErledigt = aufgaben.filter(a => 
            a.status === 'erledigt' && (
              a.erledigt_am?.slice(0, 10) === heute || 
              a.updated_date?.slice(0, 10) === heute
            )
          );
          setNotifications(prev => ({ 
            ...prev, 
            aufgaben: Math.min(neueAufgaben.length, 9),
            erledigt: Math.min(heuteErledigt.length, 9)
          }));
        }
        
        // Orange: Neue Nachrichten im Posteingang + ungelesene Zeo-Nachrichten
        let nachrichtenCount = 0;
        const postResp = await fetch(`${API_URL}/api/assistent/posteingang/mit-analyse`);
        if (postResp.ok) {
          const data = await postResp.json();
          nachrichtenCount += data.anzahl || 0;
        }
        const zeoResp = await fetch(`${API_URL}/api/assistent/zeo-nachrichten?nur_ungelesen=true`);
        if (zeoResp.ok) {
          const zeoData = await zeoResp.json();
          nachrichtenCount += zeoData.ungelesen || 0;
        }
        setNotifications(prev => ({ ...prev, nachrichten: Math.min(nachrichtenCount, 9) }));
      } catch (err) {
        console.log('Benachrichtigungen konnten nicht geladen werden');
      }
    };
    
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // ====== TABS ÖFFNEN WENN AKTIONEN AUSGEFÜHRT WERDEN ======
  const handleOpenTabs = useCallback((tabsToOpen) => {
    if (!tabsToOpen || !Array.isArray(tabsToOpen)) return;
    
    for (const tab of tabsToOpen) {
      if (tab.url) {
        openTab(tab.url, {
          title: tab.name || tab.typ,
          params: { name: tab.name, id: tab.id }
        });
        toast.success(`Tab geöffnet: ${tab.name}`);
      }
    }
  }, [openTab]);
  
  // Debug
  useEffect(() => {
    console.log('AssistentChat open state:', open);
  }, [open]);

  // ====== KONTEXTBEZOGENER VERLAUF LADEN ======
  // Wird unten nach getKontext definiert (useEffect nach getKontext)
  const [kontextVerlaufLoaded, setKontextVerlaufLoaded] = useState(false);

  // ====== MORGEN-BRIEFING ======
  // Beim ersten Chat-Öffnen des Tages automatisch Briefing laden
  useEffect(() => {
    if (!open || briefingLoaded) return;

    const today = new Date().toISOString().slice(0, 10);
    const lastBriefing = localStorage.getItem('olympus_last_briefing_date');

    if (lastBriefing === today) {
      setBriefingLoaded(true);
      return;
    }

    const loadBriefing = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/assistent/briefing`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.success && data.briefing) {
          setMessages(prev => {
            if (prev.some(m => m.isBriefing)) return prev;
            return [{ rolle: 'assistent', text: data.briefing, isBriefing: true, zusammenfassung: data.zusammenfassung }, ...prev];
          });
          localStorage.setItem('olympus_last_briefing_date', today);
        }
      } catch (e) {
        console.log('Briefing laden fehlgeschlagen:', e);
      } finally {
        setBriefingLoaded(true);
      }
    };

    loadBriefing();
  }, [open, briefingLoaded]);

  // ====== PROAKTIVE ZEO-NACHRICHTEN ======
  // Beim Chat-Öffnen ungelesene Zeo-Nachrichten als Mitteilungen anzeigen
  useEffect(() => {
    if (!open) return;

    const loadZeoNachrichten = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/assistent/zeo-nachrichten?nur_ungelesen=true`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.nachrichten?.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.filter(m => m.zeoNachrichtId).map(m => m.zeoNachrichtId));
            const neue = data.nachrichten
              .filter(n => !existingIds.has(n.id))
              .map(n => ({
                rolle: 'assistent',
                text: n.text,
                isZeoProaktiv: true,
                zeoNachrichtId: n.id,
                zeoTyp: n.typ,
                zeoKlientName: n.klient_name,
                zeoAktionen: n.aktionen,
                erstellt_am: n.erstellt_am,
              }));
            if (neue.length === 0) return prev;
            return [...neue, ...prev];
          });
          // Als gelesen markieren
          await fetch(`${API_URL}/api/assistent/zeo-nachrichten/alle-gelesen`, { method: 'POST' });
          // Notification-Counter aktualisieren
          setNotifications(prev => ({ ...prev, nachrichten: 0 }));
        }
      } catch (e) {
        console.log('Zeo-Nachrichten laden fehlgeschlagen:', e);
      }
    };

    loadZeoNachrichten();
  }, [open]);

  // ====== PROAKTIVE MUSTER-VORSCHLÄGE PRÜFEN ======
  // Prüft regelmäßig ob neue Mustervorschläge vorhanden sind
  useEffect(() => {
    const checkMusterVorschlaege = async () => {
      try {
        // Nur für Admins prüfen
        const currentUser = authAPI.getCurrentUser();
        if (!currentUser || currentUser.rolle !== 'admin') {
          return;
        }
        
        const response = await fetch(`${API_URL}/api/athena/vorschlaege`);
        const data = await response.json();
        
        if (data.success && data.anzahl > 0) {
          setMusterVorschlaege(data.anzahl);
          
          // Zeige proaktive Nachricht wenn Chat offen und noch nicht gezeigt
          if (open && !hasShownVorschlaegeBanner) {
            const bannerMsg = {
              rolle: 'assistent',
              text: `Ich habe ${data.anzahl} neue Dokumentmuster erkannt.`,
              isVorschlagBanner: true
            };
            setMessages(prev => {
              if (!prev.some(m => m.isVorschlagBanner)) {
                return [bannerMsg, ...prev];
              }
              return prev;
            });
            setHasShownVorschlaegeBanner(true);
            sessionStorage.setItem('athena_muster_banner_shown', 'true');
          }
        } else {
          setMusterVorschlaege(0);
        }
      } catch (error) {
        console.log('Mustervorschläge-Check fehlgeschlagen:', error);
      }
    };
    
    // Initial prüfen
    checkMusterVorschlaege();
    
    // Alle 60 Sekunden erneut prüfen wenn Chat offen ist
    const interval = setInterval(() => {
      if (open) {
        checkMusterVorschlaege();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [open, hasShownVorschlaegeBanner]);

  // Zeige Banner für neue Mustervorschläge wenn Chat geöffnet wird (Fallback)
  useEffect(() => {
    if (open && musterVorschlaege > 0 && !hasShownVorschlaegeBanner) {
      // Kleine Verzögerung für besseres UX
      const timer = setTimeout(() => {
        const bannerMsg = {
          rolle: 'assistent',
          text: `Ich habe ${musterVorschlaege} neue Dokumentmuster erkannt.`,
          isVorschlagBanner: true
        };
        setMessages(prev => {
          if (!prev.some(m => m.isVorschlagBanner)) {
            return [bannerMsg, ...prev];
          }
          return prev;
        });
        setHasShownVorschlaegeBanner(true);
        sessionStorage.setItem('athena_muster_banner_shown', 'true');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, musterVorschlaege, hasShownVorschlaegeBanner]);

  const getKontext = useCallback(() => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'akten' && parts[1] && parts[1] !== 'neu')
      return { typ: 'betreuungsakte', id: parts[1] };
    if (parts[0] === 'aufgaben' && parts[1] && parts[1] !== 'neu')
      return { typ: 'aufgabe', id: parts[1] };
    if (parts[0] === 'aufgaben') return { typ: 'aufgaben', id: null };
    if (parts[0] === 'personen' && parts[1]) return { typ: 'person', id: parts[1] };
    if (parts[0] === '' || path === '/') return { typ: 'dashboard', id: null };
    return { typ: null, id: null };
  }, [location.pathname]);

  // Automatisch den Kontext erfassen für bessere KI-Unterstützung
  const [currentPageContext, setCurrentPageContext] = useState(null);
  
  // Globaler Event-Handler für Kontext-Updates von Seiten
  useEffect(() => {
    const handleContextUpdate = (e) => {
      if (e.detail) {
        try {
          // Nur serialisierbare Daten speichern
          const safeContext = JSON.parse(JSON.stringify(e.detail));
          setCurrentPageContext(safeContext);
        } catch (err) {
          console.warn('Kontext-Update nicht serialisierbar:', err);
        }
      }
    };
    window.addEventListener('olympus-context-update', handleContextUpdate);
    return () => window.removeEventListener('olympus-context-update', handleContextUpdate);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ====== KONTEXTBEZOGENER VERLAUF LADEN ======
  useEffect(() => {
    if (!open || sessionId || kontextVerlaufLoaded) return;
    const kontext = getKontext();
    if (!kontext.typ || !kontext.id) return;

    setKontextVerlaufLoaded(true);
    const loadKontextVerlauf = async () => {
      try {
        const token = localStorage.getItem('olympus_token');
        const res = await fetch(
          `${API_URL}/api/assistent/sessions?kontext_typ=${kontext.typ}&kontext_id=${kontext.id}&limit=1`,
          { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
        );
        if (!res.ok) return;
        const sessions = await res.json();
        if (sessions.length === 0) return;
        const lastSession = sessions[0];
        const detailRes = await fetch(
          `${API_URL}/api/assistent/sessions/${lastSession.id}`,
          { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
        );
        if (!detailRes.ok) return;
        const detail = await detailRes.json();
        if (detail.messages?.length > 0) {
          setSessionId(lastSession.id);
          setMessages(detail.messages.map(m => ({ rolle: m.rolle, text: m.text })));
        }
      } catch (e) {
        console.log('Kontext-Verlauf laden fehlgeschlagen:', e);
      }
    };
    loadKontextVerlauf();
  }, [open, sessionId, kontextVerlaufLoaded, getKontext]);

  // Audio-Kontext "freischalten" bei erster Benutzerinteraktion
  // Das ist nötig weil Mobile-Browser Autoplay blockieren
  const unlockAudio = useCallback(() => {
    if (audioUnlocked) return;
    
    // Stille Audio-Datei (base64-encoded 100ms silence MP3)
    const silentAudio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYZfWqjAAAAAAD/+1DEAAAFeANb9BEAIf8Aqv80IABJJBBBBBGCYGAx//0w/////8zMgAEEhBAgQIEYD4+CAIAgCAYBge/4eD4fD4fy4fDAw//B8EHwfP/g+H/8Hw+H/Lh8MDAx+Dg+H/+CY+DA//Bg+D//+CAIO7wQ//BB8PggCD/4f/B///BD//8H+X///g4P///8Hw+Hz///BB8H//4OCHwfDgoKCgoKCD/+H///wfP+D//+H/+CYoLuD/w/4eDgEAQ///hINig/8HBR8EB/4f//8HBQR///h4OD////8PB//4f//D4eD////gcHw/+D5////D/4f////8EHw8P////B/4f//8PA//+H//+H/8HBf/+H//4ODAcHB8P//8Pg//+Dg//8PB///8Pw=');
    
    silentAudio.play().then(() => {
      setAudioUnlocked(true);
      silentAudio.pause();
    }).catch(() => {
      // Ignorieren - wird bei nächster Interaktion erneut versucht
    });
  }, [audioUnlocked]);

  // Bei ersten Klick im Chat Audio freischalten
  useEffect(() => {
    if (!open) return;
    
    const handleInteraction = () => {
      unlockAudio();
    };
    
    // Einmalig bei Klick im Chat-Fenster
    document.addEventListener('click', handleInteraction, { once: true });
    return () => document.removeEventListener('click', handleInteraction);
  }, [open, unlockAudio]);

  // ====== SPRACHEINGABE (Web Speech API) ======
  const startListening = () => {
    // Prüfe Browser-Unterstützung
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { 
      toast.error('Spracherkennung wird in diesem Browser nicht unterstützt. Bitte Chrome oder Edge verwenden.'); 
      return; 
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'de-DE';
      recognition.continuous = true;  // Kontinuierlich zuhören
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        toast.success('Ich höre zu...', { duration: 2000 });
      };
      
      recognition.onresult = (e) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const transcript = e.results[i][0].transcript;
          if (e.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Zeige Zwischenergebnis oder finales Ergebnis
        if (finalTranscript) {
          const currentInput = document.querySelector('[data-testid="assistent-input"]')?.value || '';
          const fullText = (currentInput + ' ' + finalTranscript).trim();
          setInput(fullText);
          // Im Gesprächsmodus: Automatisch senden nach finalem Ergebnis
          if (conversationMode) {
            recognition.stop();
            setTimeout(() => {
              handleSendWithText(fullText);
              setInput('');
            }, 300);
          }
        } else {
          setInput(prev => prev || interimTranscript);
        }
      };
      
      recognition.onend = () => { 
        setIsListening(false); 
        recognitionRef.current = null;
      };
      
      recognition.onerror = (e) => { 
        setIsListening(false); 
        recognitionRef.current = null;
        if (e.error === 'not-allowed') {
          toast.error('Mikrofonzugriff verweigert. Bitte erlauben Sie den Zugriff in den Browsereinstellungen.');
        } else if (e.error === 'service-not-allowed') {
          toast.error('Spracherkennung nicht verfügbar auf diesem Gerät. Bitte nutzen Sie Chrome auf Desktop oder tippen Sie Ihre Nachricht.');
        } else if (e.error === 'no-speech') {
          // Ignorieren - passiert wenn nichts gesagt wird
        } else if (e.error === 'network') {
          toast.error('Netzwerkfehler bei der Spracherkennung');
        } else {
          console.log('Speech recognition error:', e.error);
          // Kein Toast für unbekannte Fehler
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      toast.error('Fehler beim Starten der Spracherkennung');
      console.error('Speech recognition error:', err);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) { 
      recognitionRef.current.stop(); 
      // Automatisch senden wenn Text vorhanden
      if (input.trim()) {
        setTimeout(() => handleSend(), 300);
      }
    }
  };

  // ====== OPENAI WHISPER STT - Aufnahme und Transkription ======
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          // Transkribieren mit OpenAI Whisper
          setLoading(true);
          try {
            const result = await betreuungAPI.speechToText(audioBlob, 'de');
            if (result.success && result.text) {
              setInput(prev => (prev + ' ' + result.text).trim());
              // Automatisch senden
              setTimeout(() => {
                if (result.text.trim()) {
                  handleSendWithText(result.text.trim());
                }
              }, 300);
            }
          } catch (err) {
            console.error('Whisper STT Error:', err);
            toast.error('Spracherkennung fehlgeschlagen');
          } finally {
            setLoading(false);
          }
        }
        
        setIsRecording(false);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      
      // Toast-Info
      toast.info('Sprechen Sie jetzt... Klicken Sie erneut zum Beenden.', { duration: 2000 });
      
    } catch (err) {
      console.error('Recording error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Mikrofonzugriff verweigert');
      } else {
        toast.error('Aufnahme fehlgeschlagen');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  // ZEO Mittwald Deep-Run: minimaler Status-Poll (Produktionskorrektur
  // 05.10.2026). Bindet das Ergebnis NUR in den sichtbaren Chat ein, wenn
  // der Nutzer noch in DERSELBEN Akte ist, in der der Lauf gestartet wurde
  // -- sonst KEINE Injektion (Ergebnis bleibt serverseitig trotzdem an die
  // urspruengliche session_id/Akte gebunden und dort beim naechsten Oeffnen
  // sichtbar). Keine Chain-of-Thought, nur die finale Antwort.
  const pollZeoMittwaldDeepResult = (runId) => {
    const _gestarteteAkteId = getKontext().id;
    let versuch = 0;
    const MAX_VERSUCHE = 40; // ~ bis zu einigen Minuten, grosszuegige Intervalle
    const poll = async () => {
      versuch += 1;
      try {
        const ergebnis = await betreuungAPI.zeoMittwaldRunResult(runId);
        if (ergebnis.status === 'completed' || ergebnis.status === 'failed' || ergebnis.status === 'cancelled') {
          const nochDieselbeAkte = getKontext().id === _gestarteteAkteId;
          if (nochDieselbeAkte && ergebnis.antwort) {
            setMessages(prev => [...prev, { rolle: 'assistent', text: ergebnis.antwort }]);
          }
          return; // fertig, kein weiteres Polling
        }
      } catch (e) {
        // Technischer Fehler beim Status-Poll -- nicht weiter stoeren,
        // das Ergebnis bleibt serverseitig ohnehin persistent.
        return;
      }
      if (versuch < MAX_VERSUCHE) {
        setTimeout(poll, 5000);
      }
    };
    setTimeout(poll, 3000);
  };

  // Senden mit spezifischem Text (fuer Voice)
  const handleSendWithText = async (text) => {
    if (!text.trim() || loading) return;
    setInput('');
    
    setMessages(prev => [...prev, { rolle: 'user', text }]);
    setLoading(true);
    
    // "Einen Moment"-Ansage nach 4 Sekunden wenn noch keine Antwort
    let momentTimeout = null;
    if (autoSpeak || conversationMode) {
      momentTimeout = setTimeout(async () => {
        try {
          const selectedElevenVoice = localStorage.getItem('zeo_voice_eleven') || null;
          const momentResult = await betreuungAPI.textToSpeechElevenLabs(
            'Ich verstehe Ihr Anliegen. Geben Sie mir einen Moment, ich lese mich in die Akte ein.',
            selectedElevenVoice
          );
          if (momentResult?.audio_base64) {
            const audio = new Audio(`data:audio/mpeg;base64,${momentResult.audio_base64}`);
            audio.play().catch(() => {});
          }
        } catch (e) { /* still */ }
      }, 4000);
    }
    
    try {
      const kontext = getKontext();
      
      const response = await betreuungAPI.chatAssistent({
        nachricht: text,
        session_id: sessionId,
        modus: 'online',
        kontext_typ: kontext.typ,
        kontext_id: kontext.id,
      });
      
      // "Moment"-Ansage abbrechen falls Antwort schnell kam
      if (momentTimeout) clearTimeout(momentTimeout);
      
      if (!sessionId && response.session_id) setSessionId(response.session_id);
      
      const assistentMsg = { rolle: 'assistent', text: response.antwort };
      setMessages(prev => [...prev, assistentMsg]);

      // Automatisch vorlesen
      if ((autoSpeak || conversationMode) && response.antwort && !response.fehler) {
        setTimeout(() => speak(response.antwort), 500);
      }

      // ZEO Mittwald Deep-Run (Produktionskorrektur 05.10.2026): eine
      // vollstaendige Fallanalyse laeuft im Hintergrund weiter. Minimaler
      // P0-Status-Poll -- das fertige Ergebnis wird serverseitig OHNEHIN
      // dauerhaft in die Chat-Historie geschrieben (auch ohne Polling hier
      // beim naechsten Laden sichtbar); dieses Polling zeigt es nur sofort
      // an, ohne dass der Nutzer neu fragen/die Seite neu laden muss. Der
      // Nutzer darf waehrenddessen problemlos die Akte wechseln.
      if (response.modus === 'mittwald_deep_running' && response.run_id) {
        pollZeoMittwaldDeepResult(response.run_id);
      }
      
      // Aktions-Handling
      if (response.aktion_vorschlag) {
        setPendingAktion(response.aktion_vorschlag);
      } else if (response.aktion_ausgefuehrt) {
        setPendingAktion(null);
      }
      
    } catch (err) {
      setMessages(prev => [...prev, { 
        rolle: 'assistent', 
        text: 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut.' 
      }]);
      if (conversationMode) setTimeout(() => startListening(), 1000);
    } finally {
      setLoading(false);
    }
  };

  // ====== VORLESEN MIT ELEVENLABS TTS (PRIMAER) / PIPER TTS (FALLBACK) ======
  const speak = async (text) => {
    // Wenn bereits spricht, stoppen
    if (isSpeaking) { 
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      window.speechSynthesis?.cancel();
      setIsSpeaking(false); 
      return; 
    }
    
    // Text aufbereiten fuer natuerliche Sprachausgabe (Ohren, nicht Augen)
    let cleanText = text
      // UUIDs und technische IDs komplett entfernen
      .replace(/\(ID:?\s*[a-f0-9-]{20,}\)/gi, '')
      .replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '')
      // Versichertennummern und Aktenzeichen kuerzen
      .replace(/Versichertennummer:?\s*[A-Z0-9]{6,}/gi, '')
      .replace(/\bAz\.?\s*\d+[A-Z]*\d+\/\d+/gi, '')
      // Markdown komplett entfernen
      .replace(/\|[^|\n]*\|/g, '')
      .replace(/[-─═]{3,}/g, '')
      .replace(/#{1,6}\s?/g, '')
      .replace(/\*\*/g, '').replace(/\*/g, '')
      .replace(/>/g, '')
      .replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Alle Emoji entfernen
      .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu, '')
      .replace(/[\u{2705}\u{274C}\u{2757}\u{2139}\u{23F3}\u{2934}\u{2935}\u{25B6}\u{25C0}\u{23ED}\u{23EE}\u{23F8}\u{23F9}\u{23FA}]/gu, '')
      .replace(/\d+️⃣/g, '')
      // Geldsummen lesbarer: "3.347,42 €" -> "3347 Euro 42"
      .replace(/(\d{1,3}(?:\.\d{3})*),(\d{2})\s*€/g, (_, euros, cents) => 
        `${euros.replace(/\./g, '')} Euro ${cents}`)
      // Klammern mit nur Whitespace/Sonderzeichen entfernen
      .replace(/\(\s*\)/g, '')
      .replace(/\(\s*,?\s*\)/g, '')
      // Aufzaehlungspunkte natuerlicher
      .replace(/^\d+\.\s+/gm, '')
      .replace(/^-\s+/gm, '')
      // Zeilenumbrueche zu Pausen
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, '. ')
      // Aufraeumen
      .replace(/\s{2,}/g, ' ')
      .replace(/\.\s*\./g, '.')
      .replace(/,\s*,/g, ',')
      .replace(/:\s*\./g, '.')
      .replace(/\(\s*\)/g, '')
      .trim()
      .substring(0, 2000);
    
    if (!cleanText.trim()) return;
    
    setTtsLoading(true);
    setIsSpeaking(true);
    
    try {
      // ElevenLabs (Ela) — einzige Stimme, kein Fallback
      let audioData = null;
      let contentType = 'audio/mpeg';
      
      try {
        const selectedElevenVoice = localStorage.getItem('zeo_voice_eleven') || null;
        const elevenResult = await betreuungAPI.textToSpeechElevenLabs(cleanText, selectedElevenVoice);
        if (elevenResult && elevenResult.audio_base64) {
          audioData = elevenResult.audio_base64;
          contentType = elevenResult.content_type || 'audio/mpeg';
        }
      } catch (e) {
        console.log('ElevenLabs nicht erreichbar');
      }
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: contentType }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          if (conversationMode) {
            setTimeout(() => startListening(), 400);
          }
        };
        
        audio.onerror = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          fallbackSpeak(cleanText);
        };
        
        await audio.play().catch((err) => {
          console.warn('Audio autoplay blocked:', err);
          if (!audioUnlocked) {
            toast.info('Tippen Sie auf das Lautsprecher-Symbol zum Vorlesen', { duration: 4000 });
          }
          setIsSpeaking(false);
        });
        
        setAudioUnlocked(true);
      } else {
        // Kein Audio von ElevenLabs — still bleiben statt Roboterstimme
        setIsSpeaking(false);
      }
    } catch (err) {
      console.error('TTS error:', err);
      setIsSpeaking(false);
    } finally {
      setTtsLoading(false);
    }
  };
  
  // Fallback Browser-TTS
  const fallbackSpeak = (text) => {
    if (!window.speechSynthesis) {
      setIsSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    utterance.rate = 0.95;
    // Versuche weibliche Stimme zu finden
    const voices = window.speechSynthesis.getVoices();
    const germanFemale = voices.find(v => v.lang.includes('de') && v.name.toLowerCase().includes('female'));
    const german = voices.find(v => v.lang.includes('de'));
    if (germanFemale) utterance.voice = germanFemale;
    else if (german) utterance.voice = german;
    
    utterance.onend = () => {
      setIsSpeaking(false);
      if (conversationMode) {
        setTimeout(() => startListening(), 400);
      }
    };
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  // ====== NACHRICHT SENDEN ======
  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');

    // "Ja" als Bestätigung für pendingAktion
    const textLower = text.toLowerCase().trim();
    const jaPatterns = ['ja', 'ja bitte', 'ok', 'bestätigen', 'mach das', 'jawohl', 'genau', 'richtig', 'stimmt'];
    const neinPatterns = ['nein', 'abbrechen', 'nein danke', 'stopp', 'stop', 'nicht'];
    
    if (pendingAktion) {
      // Prüfe ob Text mit "ja" beginnt oder ein Ja-Pattern enthält
      const isJa = textLower.startsWith('ja') || jaPatterns.some(p => textLower === p);
      const isNein = neinPatterns.some(p => textLower === p || textLower.startsWith(p + ' '));
      
      if (isJa && !isNein) {
        await executeAktion(pendingAktion);
        setPendingAktion(null);
        return;
      }
      if (isNein) {
        setMessages(prev => [...prev, { rolle: 'user', text }, { rolle: 'assistent', text: 'Aktion abgebrochen.', modus: 'lokal' }]);
        setPendingAktion(null);
        return;
      }
    }
    setPendingAktion(null);

    setMessages(prev => [...prev, { rolle: 'user', text }]);
    setLoading(true);

    try {
      const kontext = getKontext();
      
      // Erweiterten Kontext mit Seitendaten senden wenn verfügbar
      const requestData = {
        nachricht: text, 
        session_id: sessionId, 
        modus: 'online',
        kontext_typ: kontext.typ, 
        kontext_id: kontext.id,
      };
      
      // Falls aktueller Seitenkontext vorhanden (z.B. Aufgaben-Details)
      if (currentPageContext) {
        try {
          // Sicherstellen dass der Kontext serialisierbar ist
          requestData.page_context = JSON.parse(JSON.stringify(currentPageContext));
        } catch (ctxErr) {
          console.warn('Seitenkontext nicht serialisierbar, wird uebersprungen:', ctxErr);
        }
      }
      
      const response = await betreuungAPI.chatAssistent(requestData);

      if (!sessionId && response.session_id) setSessionId(response.session_id);

      const msg = { rolle: 'assistent', text: response.antwort, fehler: response.fehler, isWochenbericht: response.isWochenbericht || false, modell: response.modell || null };
      setMessages(prev => [...prev, msg]);

      // Automatisches Vorlesen wenn aktiviert und keine Fehler
      if (autoSpeak && response.antwort && !response.fehler) {
        // Kurze Verzögerung für besseres UX
        setTimeout(() => speak(response.antwort), 500);
      }

      // ZEO Mittwald Deep-Run (Produktionskorrektur 05.10.2026) -- s. pollZeoMittwaldDeepResult oben.
      if (response.modus === 'mittwald_deep_running' && response.run_id) {
        pollZeoMittwaldDeepResult(response.run_id);
      }

      // Speichere Dokument-Kontext wenn vorhanden (für "Bearbeite Dokument X")
      if (response.kontext_dokument) {
        setAktiveDokumentKontext(response.kontext_dokument);
      }
      // Oder aus der Aktion (inklusive extrahierte_daten für Akte-Erstellung)
      if (response.aktion?.daten?.dokument_id || response.aktion?.daten?.extrahierte_daten) {
        setAktiveDokumentKontext({
          id: response.aktion.daten.dokument_id,
          dok_nr: response.aktion.daten.dok_nr,
          vorgeschlagener_titel: response.aktion.daten.vorgeschlagener_titel,
          batch_info: response.aktion.daten.batch_info,
          extrahierte_daten: response.aktion.daten.extrahierte_daten  // WICHTIG: Für Akte-Erstellung
        });
      }
      // Auch extrahierte_daten direkt aus der Response speichern
      if (response.extrahierte_daten) {
        setAktiveDokumentKontext(prev => ({
          ...prev,
          extrahierte_daten: response.extrahierte_daten
        }));
      }

      // Wenn Aktion vorhanden
      if (response.aktion) {
        // Informative Aktionen die keine Ausführung erfordern
        const informativeAktionen = [
          'dokument_bearbeitung_aktiv',
          'dokument_upload_anleitung',
          'wochenbericht',
          'modus_wechsel_empfehlung',
          'name_aus_dokument_erkannt',
          'name_aus_dokument',
        ];
        
        if (informativeAktionen.includes(response.aktion.typ)) {
          // Nur informativ, keine Aktion ausführen
          return;
        }
        
        // ====== TABS ÖFFNEN FÜR ERSTELLTE OBJEKTE ======
        if (response.aktion.typ === 'multi_erstellt' && response.aktion.daten?.tabs_oeffnen) {
          // Tabs öffnen für erstellte Objekte
          handleOpenTabs(response.aktion.daten.tabs_oeffnen);
          return;  // Keine weitere Aktion nötig - Objekte wurden bereits erstellt
        }
        if (response.aktion.typ === 'klient_erstellt' && response.aktion.daten?.id) {
          // Tab für neuen Klienten öffnen
          handleOpenTabs([{
            typ: 'betreuungsakte',
            id: response.aktion.daten.id,
            name: response.aktion.daten.name,
            url: `/betreuungsakten/${response.aktion.daten.id}`
          }]);
          return;  // Keine weitere Aktion nötig
        }
        
        if (response.aktion.bestaetigung_noetig) {
          // Warte auf Bestätigung
          setPendingAktion(response.aktion);
        } else {
          // Direkt ausführen ohne Bestätigung
          await executeAktion(response.aktion);
        }
      }
    } catch (e) {
      const errorMsg = e.message || 'Unbekannter Fehler';
      console.error('Zeo Chat Fehler:', e);
      let userMsg = errorMsg;
      if (errorMsg.includes('clone') || errorMsg.includes('serialize')) {
        userMsg = 'Datenverarbeitungsfehler. Bitte versuche es erneut.';
      } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
        userMsg = 'Netzwerkfehler — Server nicht erreichbar.';
      }
      setMessages(prev => [...prev, { rolle: 'assistent', text: `Fehler: ${userMsg}`, fehler: true }]);
    } finally {
      setLoading(false);
    }
  };


  const executeAktion = async (aktion) => {
    setLoading(true);
    try {
      const kontext = getKontext();
      
      // Dokument-ID aus dem aktiven Kontext hinzufügen wenn vorhanden
      const aktionDaten = { ...aktion.daten };
      if (aktiveDokumentKontext?.id && !aktionDaten.dokument_id) {
        aktionDaten.dokument_id = aktiveDokumentKontext.id;
        aktionDaten.vorgeschlagener_titel = aktiveDokumentKontext.vorgeschlagener_titel;
      }
      
      const result = await betreuungAPI.executeAssistentAktion({
        aktion: aktion.typ, daten: aktionDaten,
        bestaetigt: true, kontext_typ: kontext.typ, kontext_id: kontext.id,
      });
      setMessages(prev => [
        ...prev,
        { rolle: 'user', text: 'Ja, bitte ausführen.', modus: 'lokal' },
        { rolle: 'assistent', text: result.antwort || 'Aktion ausgeführt.', modus: 'lokal',
          aktion_erfolg: result.success }
      ]);
      if (result.success) {
        toast.success('Aktion ausgeführt');
        // Nach erfolgreicher Ablegen-Aktion: Kontext zurücksetzen
        if (aktion.typ === 'dokument_ablegen' || aktion.typ === 'dokument_zuordnen_und_ablegen') {
          setAktiveDokumentKontext(null);
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, { rolle: 'assistent', text: `Fehler: ${e.message}`, fehler: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => { setMessages([]); setSessionId(null); setPendingAktion(null); setAktiveDokumentKontext(null); setBriefingLoaded(true); };

  // Datei-Upload Handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Dateigrößen-Check (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }
    
    // Erlaubte Dateitypen
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.txt', '.doc', '.docx'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error('Dateityp nicht unterstützt. Erlaubt: PDF, Bilder, TXT, DOC');
      return;
    }
    
    setUploadingFile(true);
    setMessages(prev => [...prev, { 
      rolle: 'user', 
      text: `📎 Datei hochgeladen: ${file.name}`,
      isFile: true
    }]);
    
    try {
      const kontext = getKontext();
      // WICHTIG: Session-ID mitsenden für isolierten Kontext
      const currentSessionId = sessionId || `upload_${Date.now()}`;
      if (!sessionId) setSessionId(currentSessionId);
      
      const response = await betreuungAPI.chatMitDatei(file, '', kontext.typ || '', kontext.id || '', currentSessionId);
      
      if (response.success) {
        const msg = { 
          rolle: 'assistent', 
          text: response.antwort,
          modus: 'lokal'
        };
        setMessages(prev => [...prev, msg]);
        
        // Dokument-Kontext speichern
        if (response.kontext_dokument) {
          setAktiveDokumentKontext(response.kontext_dokument);
        }
        
        toast.success('Dokument analysiert');
      } else {
        setMessages(prev => [...prev, { 
          rolle: 'assistent', 
          text: response.antwort || 'Fehler beim Verarbeiten der Datei', 
          fehler: true 
        }]);
      }
    } catch (e) {
      console.error('File upload error:', e);
      setMessages(prev => [...prev, { 
        rolle: 'assistent', 
        text: `Fehler beim Hochladen: ${e.message}`, 
        fehler: true 
      }]);
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingFile(false);
      // Input zurücksetzen
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Markdown-Renderer für Briefing & Wochenbericht
  const renderMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let tableRows = [];
    let inTable = false;

    // Process bold text - returns simple string or JSX elements
    const renderBold = (str) => {
      if (!str) return null;
      if (!str.includes('**')) {
        return str.replace(/\*/g, '');
      }
      const parts = str.split(/\*\*(.*?)\*\*/g);
      return parts.map((part, pi) => {
        if (pi % 2 === 1) {
          // Bold text
          return <strong key={pi} className="text-slate-800">{part}</strong>;
        }
        // Normal text - clean asterisks
        return part.replace(/\*/g, '') || null;
      }).filter(item => item !== null && item !== '');
    };

    // Helper to create table from collected rows
    const createTable = (key) => {
      if (tableRows.length === 0) return null;
      const header = tableRows[0];
      const dataRows = tableRows.slice(1);
      
      return (
        <table key={key} className="w-full text-xs border-collapse my-2">
          <thead>
            <tr>
              {header.map((h, hi) => (
                <th key={hi} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold text-slate-700">
                  {renderBold(h.trim())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-slate-200 px-2 py-1">
                    {renderBold(cell.trim())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    };

    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      
      // Table row detection
      if (line.startsWith('|') && line.endsWith('|')) {
        if (line.includes('---')) continue; // Skip header separator
        const cells = line.split('|').filter(c => c.trim());
        tableRows.push(cells);
        inTable = true;
        continue;
      }
      
      // End of table - flush it
      if (inTable && tableRows.length > 0) {
        const table = createTable(`t-${li}`);
        if (table) elements.push(table);
        tableRows = [];
        inTable = false;
      }
      
      // Other markdown elements
      if (line.startsWith('## ')) { 
        elements.push(<p key={li} className="text-base font-bold text-slate-800 mb-1">{line.replace('## ', '')}</p>); 
        continue; 
      }
      if (line.startsWith('### ')) { 
        elements.push(<p key={li} className="text-sm font-semibold text-slate-700 mt-2">{line.replace('### ', '')}</p>); 
        continue; 
      }
      if (line.startsWith('- ')) { 
        elements.push(<p key={li} className="ml-2 text-sm text-slate-600">{renderBold(line.replace('- ', ''))}</p>); 
        continue; 
      }
      if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) { 
        elements.push(<p key={li} className="text-xs text-slate-400 italic">{line.replace(/^\*|\*$/g, '')}</p>); 
        continue; 
      }
      if (line.startsWith('---')) { 
        elements.push(<hr key={li} className="my-2 border-slate-200" />); 
        continue; 
      }
      if (line.trim()) { 
        elements.push(<p key={li} className="text-sm">{renderBold(line)}</p>); 
      }
    }
    
    // Flush any remaining table at end of text
    if (tableRows.length > 0) {
      const table = createTable('t-end');
      if (table) elements.push(table);
    }
    
    return elements;
  };

  // Handler für das Öffnen des Chats
  const handleOpenChat = useCallback(() => {
    console.log('Assistant button clicked');
    // ZEO-Arbeitsbereich (Prod-Freigabe 09.10.2026): der Floating-ZEO-Button oeffnet IMMER die grosse
    // Arbeitsoberflaeche (/zeo) in einem neuen Tab -- bei geoeffneter Akte mit harter Klientenbindung,
    // sonst Buero-Scope. Das alte Panel bleibt technisch bestehen, ist aber nicht mehr das Standardziel.
    if (typeof window !== 'undefined') {
      const kontextJetzt = getKontext();
      const params = kontextJetzt.typ === 'betreuungsakte' && kontextJetzt.id
        ? `scope=client&klient_id=${encodeURIComponent(kontextJetzt.id)}`
        : 'scope=office';
      window.open(`/zeo?${params}`, '_blank', 'noopener,noreferrer');
      return;
    }
    setOpen(true);
    // Unlock audio on user interaction
    if (!audioUnlocked) {
      const silentAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
      silentAudio.play().then(() => {
        setAudioUnlocked(true);
        console.log('Audio unlocked on chat open');
      }).catch(() => {});
    }
  }, [audioUnlocked, getKontext]);

  if (!open) {
    return (
      <FloatingKIButton 
        onOpen={handleOpenChat}
        notifications={notifications}
      />
    );
  }

  return (
    <>
      <div className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-[420px] h-[100dvh] sm:h-[600px] bg-white sm:rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        data-testid="assistent-panel"
        onClick={() => showVoiceMenu && setShowVoiceMenu(false)}>
        {/* Header */}
        <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-md">
              <ZeoIcon size={32} />
            </div>
            <div>
              <span className="font-semibold text-sm block leading-tight">Zeo</span>
              <span className="text-[10px] text-slate-400 leading-tight">Olympus KI</span>
            </div>
            {conversationMode && (
              <Badge variant="outline" className="text-xs border-green-400/50 bg-green-500/30 text-green-300 animate-pulse">
                <Mic className="w-3 h-3 mr-1" />Gespräch
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Auto-Vorlesen Toggle */}
            <button 
              onClick={() => setAutoSpeak(!autoSpeak)} 
              className={`p-1.5 rounded transition-colors ${autoSpeak ? 'bg-green-500/30 text-green-300' : 'hover:bg-white/10'}`}
              title={autoSpeak ? 'Automatisches Vorlesen: AN' : 'Automatisches Vorlesen: AUS'}
              data-testid="auto-speak-toggle"
            >
              {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            {/* Stimmen-Auswahl - ElevenLabs */}
            {elevenLabsVoices.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                  className={`p-1.5 rounded transition-colors ${showVoiceMenu ? 'bg-violet-500/30 text-violet-300' : 'hover:bg-white/10'}`}
                  title={`Stimme: ${elevenLabsVoices.find(v => v.voice_id === localStorage.getItem('zeo_voice_eleven'))?.name || 'Ela'}`}
                  data-testid="voice-selector-btn"
                >
                  <Mic className="w-4 h-4" />
                </button>
                {showVoiceMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 w-64 py-1 max-h-80 overflow-y-auto" data-testid="voice-menu">
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Zeo Stimme</div>
                    {elevenLabsVoices.map(v => {
                      const isSelected = localStorage.getItem('zeo_voice_eleven') === v.voice_id;
                      return (
                        <button
                          key={v.voice_id}
                          onClick={() => {
                            localStorage.setItem('zeo_voice_eleven', v.voice_id);
                            setShowVoiceMenu(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors flex items-center justify-between ${isSelected ? 'bg-emerald-500/15 text-emerald-300' : ''}`}
                          data-testid={`voice-option-eleven-${v.voice_id}`}
                        >
                          <div>
                            <div className="font-medium">{v.name}</div>
                            <div className="text-[10px] text-slate-400">{v.labels?.accent || v.category || ''}</div>
                          </div>
                          {isSelected && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                        </button>
                      );
                    })}
                    <div className="border-t border-slate-600 mt-1 px-3 py-1.5">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> ElevenLabs Creator DPA</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button onClick={handleNewChat} className="p-1.5 hover:bg-white/10 rounded" title="Neuer Chat">
              <MessageSquarePlus className="w-4 h-4" />
            </button>
            <button onClick={() => { setOpen(false); openTab('/zeo-verlauf', { label: 'Zeo Verlauf' }); }} className="p-1.5 hover:bg-white/10 rounded" title="Zeo Verlauf / Archiv" data-testid="zeo-verlauf-btn">
              <History className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} className="p-2 hover:bg-red-500/20 bg-white/10 rounded-lg transition-colors" data-testid="assistent-close-btn" title="Chat schließen">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Sicherheits-Info */}
        <div className="px-3 py-1.5 text-xs flex items-center gap-1.5 shrink-0 bg-green-50 text-green-700">
          <Shield className="w-3 h-3" />Keine Daten verlassen das System &middot; Für externe Recherche nutzen Sie den Recherche-Button
        </div>

        {/* Benachrichtigungen-Übersicht */}
        {(notifications.aufgaben > 0 || notifications.nachrichten > 0 || notifications.erledigt > 0) && !dismissedNotifications && (
          <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2 text-xs shrink-0">
            {notifications.aufgaben > 0 && (
              <button
                onClick={() => { setOpen(false); navigate('/aufgaben'); }}
                className="flex items-center gap-1.5 text-red-600 hover:bg-red-50 px-2 py-1 rounded-md transition-colors"
                data-testid="notif-aufgaben"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{notifications.aufgaben} neue Aufgabe{notifications.aufgaben > 1 ? 'n' : ''}</span>
              </button>
            )}
            {notifications.nachrichten > 0 && (
              <button
                onClick={() => { setOpen(false); navigate('/posteingang'); }}
                className="flex items-center gap-1.5 text-orange-600 hover:bg-orange-50 px-2 py-1 rounded-md transition-colors"
                data-testid="notif-nachrichten"
              >
                <Mail className="w-3.5 h-3.5" />
                <span>{notifications.nachrichten} neue Nachricht{notifications.nachrichten > 1 ? 'en' : ''}</span>
              </button>
            )}
            {notifications.erledigt > 0 && (
              <button
                onClick={() => { setOpen(false); navigate('/aufgaben'); }}
                className="flex items-center gap-1.5 text-green-600 hover:bg-green-50 px-2 py-1 rounded-md transition-colors"
                data-testid="notif-erledigt"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                <span>{notifications.erledigt} erledigt</span>
              </button>
            )}
            <button
              onClick={() => setDismissedNotifications(true)}
              className="ml-auto p-1 hover:bg-slate-200 rounded transition-colors shrink-0"
              title="Ausblenden"
              data-testid="notif-dismiss"
            >
              <X className="w-3 h-3 text-slate-400" />
            </button>
          </div>
        )}

        {/* Audio-Freigabe-Hinweis */}
        {autoSpeak && !audioUnlocked && (
          <button 
            onClick={() => {
              const silentAudio = new Audio("data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA");
              silentAudio.play().then(() => {
                setAudioUnlocked(true);
                toast.success('Sprachausgabe aktiviert!', { duration: 2000 });
              }).catch(() => {
                toast.error('Audio-Freigabe fehlgeschlagen');
              });
            }}
            className="mx-3 mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-center gap-2 hover:bg-blue-100 transition-colors"
          >
            <Volume2 className="w-4 h-4 shrink-0" />
            <span>Klicken Sie hier, um die <strong>Sprachausgabe zu aktivieren</strong></span>
          </button>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <ZeoIcon size={48} className="mx-auto mb-3" />
              <p className="text-sm text-slate-500 font-medium">Hallo, ich bin Zeo!</p>
              <p className="text-xs text-slate-400 mt-1">Wie kann ich dir helfen?</p>
              <div className="mt-3 space-y-1.5">
                {['Welche Aufgaben sind offen?', 'Erstelle Aufgabe: Antrag einreichen', 'Übersicht der Akten'].map(q => (
                  <button key={q} onClick={() => setInput(q)}
                    className="block w-full text-left text-xs px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.rolle === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                msg.rolle === 'user' ? 'bg-slate-800 text-white rounded-br-md'
                  : msg.fehler ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                  : msg.aktion_erfolg ? 'bg-green-50 text-green-800 border border-green-200 rounded-bl-md'
                  : 'bg-slate-100 text-slate-800 rounded-bl-md'
              }`}>
                {msg.rolle === 'assistent' && (
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1">
                      {(msg.modell || msg.modus) && (
                        <button
                          onClick={(e) => {
                            const el = e.currentTarget.querySelector('.model-tooltip');
                            if (el) el.classList.toggle('hidden');
                          }}
                          className="relative p-0.5 rounded hover:bg-slate-200/50 transition-colors group"
                          title="Modell anzeigen"
                          data-testid="model-info-btn"
                        >
                          <Star className={`w-3.5 h-3.5 ${msg.modell === 'claude' ? 'text-violet-400' : 'text-emerald-400'}`} />
                          <span className="model-tooltip hidden absolute left-0 top-full mt-1 z-10 text-[10px] px-2 py-1 rounded bg-slate-800 text-white whitespace-nowrap shadow-lg">
                            {msg.modell === 'claude' ? 'Claude Sonnet' : msg.modell === 'ollama' ? 'Gemma 4 (Lokal)' : msg.modus === 'online' ? 'Online' : 'Lokal'}
                          </span>
                        </button>
                      )}
                      {msg.aktion_erfolg && <CheckCircle className="w-3 h-3 text-green-600" />}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => speak(msg.text)}
                        disabled={ttsLoading}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                          isSpeaking 
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                            : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                        } ${ttsLoading ? 'opacity-50' : ''}`} 
                        title={isSpeaking ? 'Stoppen' : `Vorlesen (${elevenLabsActive ? 'ElevenLabs' : availableVoices.find(v => v.id === selectedVoice)?.name || 'Piper'})`}
                        data-testid="vorlesen-btn">
                        {ttsLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : isSpeaking ? (
                          <VolumeX className="w-3.5 h-3.5" />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5" />
                        )}
                        <span>{ttsLoading ? '...' : isSpeaking ? 'Stopp' : 'Vorlesen'}</span>
                      </button>
                      {isSpeaking && (
                        <button onClick={() => {
                          if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
                          window.speechSynthesis?.cancel();
                          setIsSpeaking(false);
                          setTtsLoading(false);
                          setTimeout(() => speak(msg.text), 200);
                        }}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                          title="Sprachausgabe neu starten"
                          data-testid="tts-restart-btn">
                          <RotateCw className="w-3.5 h-3.5" />
                          <span>Neu</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words leading-relaxed">
                  {msg.isBriefing ? (
                    <div data-testid="morgen-briefing">
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                        <span className="text-lg">&#9728;&#65039;</span>
                        <span className="font-bold text-slate-800">Tagesbriefing</span>
                        {msg.zusammenfassung?.hat_dringendes && (
                          <span className="ml-auto text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">Dringend</span>
                        )}
                      </div>
                      <div className="text-sm space-y-1 briefing-content">
                        {renderMarkdown(msg.text)}
                      </div>
                    </div>
                  ) : msg.isWochenbericht ? (
                    <div data-testid="wochenbericht">
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                        <span className="text-lg">&#128203;</span>
                        <span className="font-bold text-slate-800">Wochenbericht</span>
                        <button
                          onClick={() => { const w = window.open('', '_blank'); if (w) { w.document.write(`<html><head><title>Wochenbericht</title><style>body{font-family:system-ui,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.6}h2{border-bottom:2px solid #333;padding-bottom:8px}h3{color:#555;margin-top:24px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ddd;padding:8px;text-align:left}strong{color:#333}</style></head><body>${msg.text.replace(/## /g,'<h2>').replace(/### /g,'<h3>').replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/\n- /g,'<br>- ').replace(/\n\|/g,'<br>|').replace(/\n/g,'<br>')}</body></html>`); w.document.close(); w.print(); } }}
                          className="ml-auto text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium px-2 py-0.5 rounded-full transition-colors cursor-pointer"
                          data-testid="wochenbericht-drucken"
                        >Drucken</button>
                      </div>
                      <div className="text-sm space-y-1">
                        {renderMarkdown(msg.text)}
                      </div>
                    </div>
                  ) : msg.isZeoProaktiv ? (
                    <div data-testid="zeo-proaktiv-nachricht">
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                        <Bot className="w-4 h-4 text-violet-500" />
                        <span className="font-bold text-slate-800">
                          {msg.zeoTyp === 'bescheid' ? 'Bescheid verarbeitet' : 'Post verarbeitet'}
                        </span>
                        {msg.zeoKlientName && (
                          <span className="ml-auto text-[10px] bg-violet-100 text-violet-700 font-medium px-2 py-0.5 rounded-full">{msg.zeoKlientName}</span>
                        )}
                      </div>
                      <div className="text-sm text-slate-700 whitespace-pre-line mb-2">{msg.text}</div>
                      {msg.zeoAktionen?.length > 0 && (
                        <div className="space-y-1">
                          {msg.zeoAktionen.map((a, ai) => (
                            <div key={ai} className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                              <CheckCircle className="w-3 h-3" />
                              {a}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : msg.isVorschlagBanner ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-orange-500" />
                        <span className="font-semibold">Neue Muster erkannt!</span>
                      </div>
                      <p className="mb-3">
                        Ich habe <strong>{musterVorschlaege} neue Dokumentmuster</strong> erkannt, die ich mir merken könnte. Möchten Sie diese überprüfen?
                      </p>
                      <button 
                        onClick={() => {
                          setOpen(false);
                          navigate('/admin/athena-brain');
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Lightbulb className="w-3.5 h-3.5" />
                        Zur Zeo Brain →
                      </button>
                    </div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%]">
                <div className="flex items-center gap-3">
                  <div className="relative w-5 h-5">
                    <div className="absolute inset-0 rounded-full border-2 border-violet-200" />
                    <div className="absolute inset-0 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-slate-700 font-medium transition-all duration-300">
                      {['Zeo arbeitet...', 'Daten werden geladen...', 'Tools werden ausgefuehrt...', 'Analyse laeuft...', 'Antwort wird erstellt...'][loadingPhase]}
                    </span>
                    <span className="text-[10px] text-slate-400">192 Tools verfuegbar</span>
                  </div>
                </div>
                <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-violet-400 via-indigo-400 to-violet-400 rounded-full animate-loading-bar" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Pending Aktion Hinweis */}
        {pendingAktion && (
          <div className="mx-3 mb-1 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            <span>Aktion wartet auf Bestätigung. Sagen Sie <strong>"Ja"</strong> oder <strong>"Nein"</strong>.</span>
          </div>
        )}

        {/* Input */}
        <div className="border-t bg-white px-3 py-2 shrink-0">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.doc,.docx"
            className="hidden"
            data-testid="file-input"
          />
          
          <div className="flex items-end gap-2">
            {/* Gesprächsmodus Button */}
            <button
              onClick={() => {
                const newMode = !conversationMode;
                setConversationMode(newMode);
                if (newMode) {
                  setAutoSpeak(true);
                  toast.success('Gesprächsmodus aktiviert — sprechen Sie mit Zeo', { duration: 3000 });
                  setTimeout(() => startListening(), 300);
                } else {
                  if (recognitionRef.current) recognitionRef.current.stop();
                  if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
                  window.speechSynthesis?.cancel();
                  setIsListening(false);
                  setIsSpeaking(false);
                  toast.info('Gesprächsmodus beendet', { duration: 2000 });
                }
              }}
              className={`shrink-0 h-[38px] px-3 rounded-lg flex items-center gap-1.5 transition-all text-xs font-medium ${
                conversationMode 
                  ? 'bg-green-500 text-white animate-pulse shadow-lg shadow-green-500/30' 
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700'
              }`}
              title={conversationMode ? 'Gesprächsmodus beenden' : 'Gesprächsmodus starten: Sprechen Sie mit Zeo'}
              data-testid="conversation-mode-btn">
              {conversationMode ? (
                <>
                  <Volume2 className="w-4 h-4" />
                  <span>Gespräch</span>
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  <span>Gespräch</span>
                </>
              )}
            </button>

            {/* Mikrofon Button - OpenAI Whisper */}
            <button
              onClick={isRecording ? stopRecording : (isListening ? stopListening : startRecording)}
              className={`shrink-0 h-[38px] w-[38px] rounded-lg flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/50' 
                  : isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-gradient-to-br from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
              }`}
              title={isRecording ? 'Aufnahme beenden' : 'Mit Zeo sprechen (OpenAI Whisper)'}
              data-testid="mic-btn">
              {isRecording ? (
                <div className="relative">
                  <MicOff className="w-4 h-4" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-ping" />
                </div>
              ) : isListening ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>
            
            {/* Datei-Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile || loading}
              className={`shrink-0 h-[38px] w-[38px] rounded-lg flex items-center justify-center transition-all ${
                uploadingFile 
                  ? 'bg-blue-100 text-blue-500' 
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-blue-600'
              }`}
              title="Dokument/Bild hochladen"
              data-testid="file-upload-btn">
              {uploadingFile ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Paperclip className="w-4 h-4" />
              )}
            </button>
            
            <Textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={isListening ? 'Ich höre zu...' : uploadingFile ? 'Verarbeite Datei...' : 'Frage stellen oder Befehl geben...'}
              rows={1} className="resize-none text-sm min-h-[38px] max-h-[120px]" data-testid="assistent-input" />
            <Button size="sm" onClick={handleSend} disabled={!input.trim() || loading || uploadingFile}
              className="shrink-0 h-[38px] w-[38px] p-0" data-testid="assistent-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Upload-Hinweis */}
          <div className="mt-1.5 text-[10px] text-slate-400 flex items-center gap-1">
            <Paperclip className="w-3 h-3" />
            <span>PDF, Bilder, Beschlüsse direkt hochladen</span>
          </div>
        </div>
      </div>
    </>
  );
}
